<template>
    <div
        :id="id"
        ref="container"
        :data-testid="testId || 'input-dropdown-wrapper'"
        class="tl-input-dropdown-wrapper"
    >
        <FloatLabel
            :id="id"
            :label="label"
        >
            <div
                class="tl-input-dropdown"
                :class="{
                    'tl-input-filled': filled,
                    'tl-input-disabled': disabled,
                    'tl-input-focus': dropdownOpen,
                    'tl-input-invalid': !!error,
                    'tl-input-multiline': multiline
                }"
                role="combobox"
                aria-haspopup="listbox"
                :aria-expanded="dropdownOpen"
                :aria-owns="`${id}-listbox`"
                :aria-controls="`${id}-listbox`"
                :aria-disabled="disabled"
                :aria-invalid="!!error"
                :aria-labelledby="`${id}-label`"
                :tabindex="!disabled ? 0 : -1"
                @click="handleContainerClick(false)"
                @keydown="handleKeyDown"
                @blur="handleBlur"
            >
                <span
                    class="tl-input-dropdown-label"
                    role="textbox"
                    :aria-readonly="true"
                    :aria-label="getFilledLabel || label"
                    :title="getFilledLabel || label"
                >
                    <slot
                        v-if="getValue"
                        name="dropdown-selected-option"
                        :selected-item-label="getFilledLabel"
                        :selected-item-value="getValue"
                    >
                        <div class="tl-input-dropdown-option-label">
                            <Words :oneline="!multiline">
                                {{ getFilledLabel || '&nbsp;' }}
                            </Words>
                        </div>
                    </slot>
                    <div
                        v-else
                        class="tl-input-dropdown-placeholder"
                    >
                        <Words :oneline="!multiline">
                            {{ placeholder || '&nbsp;' }}
                        </Words>
                    </div>
                </span>

                <i
                    v-if="filled && !disabled && !required"
                    class="tl-input-dropdown-clear-icon mi-close"
                    @click.stop="handleClear"
                />
                <span
                    v-if="optionsLoading"
                    class="tl-input-dropdown-icon"
                >
                    <LoadingCircle />
                </span>
                <i
                    v-else
                    class="tl-input-dropdown-icon mi-chevron-down"
                />
            </div>
        </FloatLabel>

        <Teleport
            :to="toTeleportId || scrollParent || '#app'"
            defer
        >
            <transition
                name="tl-input-dropdown-transition"
                mode="out-in"
                @enter="onOverlayEnter"
                @after-leave="onOverlayAfterLeave"
            >
                <div
                    v-if="dropdownOpen"
                    :id="`${id}-listbox`"
                    ref="dropdownOverlay"
                    class="tl-input-dropdown-overlay"
                    :style="{
                        ...floatingStyles,
                        width: dropdownContentWidth ? `${dropdownContentWidth}px` : '100%',
                        maxHeight: dropdownContentHeight ? `${dropdownContentHeight}px` : '',
                        zIndex: overlayZindex
                    }"
                    :aria-hidden="!dropdownOpen"
                >
                    <label
                        v-if="isSearchable"
                        class="tl-input-dropdown-search tl-input-dropdown-option"
                    >
                        <input
                            ref="searchRef"
                            v-model="query"
                            type="search"
                            :placeholder="t('search')"
                            role="combobox"
                            aria-autocomplete="list"
                            :aria-controls="`${id}-listbox`"
                            @input="onSearchChange"
                            @keydown="handleKeyDown"
                        />
                    </label>
                    <div
                        class="tl-input-dropdown-list-container"
                        tabindex="-1"
                    >
                        <ul
                            ref="listbox"
                            class="tl-input-dropdown-list"
                            role="list"
                        >
                            <li
                                v-for="(item, index) in filteredItems"
                                :key="item[keyField ? keyField : 'value']"
                                class="tl-input-dropdown-option"
                                :class="{
                                    'tl-input-option-selected': isActive(item),
                                    'tl-input-option-multiline': multiline,
                                    'tl-input-option-highlighted': highlightedOptionIndex === index
                                }"
                                data-testid="input-dropdown-options-item"
                                @click.prevent="handleSelect(item)"
                                @mousemove="handleOptionMouseOver(index)"
                            >
                                <slot
                                    name="dropdown-option"
                                    :item="item"
                                >
                                    <Position
                                        v-if="checkmark"
                                        x="center"
                                        y="center"
                                        class="tl-input-dropdown-checkmark"
                                    >
                                        <CheckTrigger :model-value="isActive(item)" />
                                    </Position>
                                    <Words
                                        :oneline="!multiline"
                                        class="text-break"
                                    >{{ itemLabel ? itemLabel(item) : item }}</Words>
                                </slot>
                            </li>
                        </ul>
                    </div>
                </div>
            </transition>
        </Teleport>

        <ErrorText
            v-if="error && !dropdownOpen"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed, nextTick, onUnmounted, useId } from 'vue'
import ErrorText from 'widget-components/components/ErrorText'
import { onClickOutside, promiseTimeout, until, type OnClickOutsideHandler } from '@vueuse/core'
import isIframe from 'widget-components/utilities/isIframe'
import { useI18n } from 'widget-components/utilities/i18n'
import { clamp, isNil } from 'ramda'
import { clone } from 'widget-components/utilities/clone'
import equals from 'fast-deep-equal/es6'
import Position from '../Position/Position.vue'
import CheckTrigger from '../InputCheckbox/CheckTrigger.vue'
import type { InputDropdownItem, InputDropdownProps } from './types'
import Words from 'widget-components/components/Words'
import FloatLabel from '../FloatLabel/FloatLabel.vue'
import { autoPlacement, autoUpdate, offset, type Placement, size, useFloating } from '@floating-ui/vue'
import useTeleport from 'widget-components/composables/teleport/useTeleport'
import LoadingCircle from '../LoadingCircle/LoadingCircle.vue'
import { getCssVar } from 'widget-components/utilities/getCssVar'
import { getScrollParent } from 'widget-components/utilities/get-scroll-parent'

const DROPDOWN_MAX_HEIGHT = 400

const {
    toTeleportId,
} = useTeleport()
const overlayZindex = ref<string>('-1')
const container = ref<HTMLDivElement>()
const dropdownOverlay = ref<HTMLDivElement>()
const searchRef = ref<HTMLInputElement>()
const dropdownContentHeight = ref<number | null>(null)
const dropdownContentWidth = ref<number | null>(null)
const id = computed(() => {
    return props.id ? `${props.id}-input` : useId()
})
const highlightedOptionIndex = ref<number>(-1)
const listbox = ref<HTMLUListElement>()
const scrollParent = ref<Element | null>(null)

defineOptions({ inheritAttrs: false })
const props = defineProps<InputDropdownProps & {
    required?: boolean
}>()

const emit = defineEmits(['update:modelValue', 'clear', 'dropdown-open'])

const query = ref('')
const dropdownOpen = ref(false)
const model = ref<InputDropdownProps['modelValue']>(props.multiple ? [] : undefined)

const filled = computed(() => props.multiple ? model.value?.length : !!model.value)

const isSearchable = computed(() => {
    return (props.items || []).length > 5
})

const placement = ref<Placement>('bottom')

const { floatingStyles, update: updateDropdownOverlay, isPositioned } = useFloating(container, dropdownOverlay, {
    placement,
    transform: false,
    open: dropdownOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
        offset(3),
        autoPlacement({
            elementContext: 'reference',
            autoAlignment: false,
            allowedPlacements: ['top', 'bottom'],
        }),
        size({
            apply({ availableHeight: _availableHeight, rects }) {
                const realHeightOfDropdownContent = dropdownOverlay.value?.querySelector('.tl-input-dropdown-overlay')?.clientHeight || 9999 // dummy value
                const height = Math.min(_availableHeight, realHeightOfDropdownContent, DROPDOWN_MAX_HEIGHT)
                dropdownContentHeight.value = height

                dropdownContentWidth.value = rects.reference.width
            },
        }),
    ],
})

const { t } = useI18n()

const filteredItems = computed(() => {
    if (isSearchable.value && query.value) {
        const queryValue = query.value.toLowerCase()

        return props.items?.filter((el) => {
            const label = props.itemLabel(el).toLowerCase()
            const value = props.itemValue(el).toLowerCase()

            return label.includes(queryValue) || value.includes(queryValue)
        })
    }

    return props.items
})

const selectedOptions = computed(() => {
    if (props.multiple) {
        return [...(model.value as InputDropdownItem[]).map((el) => props.itemLabel(el))].join(', ')
    }
    return model.value ? props.itemLabel(model.value) : ''
})

const selectedOptionsValue = computed(() => {
    if (props.multiple) {
        return [...(model.value as InputDropdownItem[]).map((el) => props.itemValue(el))].join(', ')
    }
    return model.value ? props.itemValue(model.value) : ''
})

const getFilledLabel = computed(() => {
    if (props.multiple) {
        return model.value?.length ? selectedOptions.value : ''
    }

    return model.value ? selectedOptions.value : ''
})

const getValue = computed(() => {
    if (props.multiple) {
        return model.value?.length ? selectedOptionsValue.value : ''
    }

    return model.value ? selectedOptionsValue.value : ''
})

const isActive = (item: InputDropdownItem) => {
    if (props.multiple) {
        return !!(model.value as InputDropdownItem[]).find((el) => props.itemValue(el) === props.itemValue(item))
    }

    return model.value && props.itemValue(model.value) === props.itemValue(item)
}

const handleMultiselect = (item: InputDropdownItem) => {
    const exists = (model.value as InputDropdownItem[]).find((el) => props.itemValue(el) === props.itemValue(item))

    if (exists) {
        model.value = (model.value as InputDropdownItem[]).filter((el) => props.itemValue(el) !== props.itemValue(item))
    } else {
        model.value = [...model.value, item]
    }
}

const handleSelect = (item: InputDropdownItem, autoClose = true) => {
    if (props.multiple) {
        handleMultiselect(item)
        updateDropdownOverlay()
        focusContainer()
    } else {
        model.value = item
        if (autoClose) {
            handleDropdownClose()
        }
    }
}

let stopClickOutsideVueUse: (() => void) | undefined
const stopClickOutside = () => {
    if (stopClickOutsideVueUse && typeof stopClickOutsideVueUse === 'function') {
        stopClickOutsideVueUse()
    }
    window.removeEventListener('blur', handleDropdownCloseEvent)
}

onUnmounted(() => {
    stopClickOutside()
})


const toggleDropdownVisibility = (value?: boolean) => {
    dropdownOpen.value = value ?? !dropdownOpen.value

    if (dropdownOpen.value) {
        emit('dropdown-open')
        stopClickOutsideVueUse = onClickOutside(container, handleDropdownClose as OnClickOutsideHandler, { detectIframe: true, ignore: [dropdownOverlay] })

        if (isIframe) {
            window.addEventListener('blur', handleDropdownCloseEvent, { passive: true })
        }
    } else {
        stopClickOutside()
    }
}

const findPrevOptionIndex = (index: number) => {
    return index > 0 ? index - 1 : index
}

const findNextOptionIndex = (index: number) => {
    const lastIndex = Math.max((filteredItems.value?.length ?? 0) - 1, 0)
    const val = index !== lastIndex ? index + 1 : index

    return clamp(0, lastIndex, val)
}

const updateHighlightedOption = (index: number) => {
    highlightedOptionIndex.value = index

    if (listbox.value) {
        nextTick(() => {
            listbox.value?.children[index]?.scrollIntoView({ block: 'nearest', inline: 'start' })
        })
    }
}

const handleDropdownOpen = () => {
    if (props.disabled) {
        return
    }

    toggleDropdownVisibility(true)
}

const handleContainerClick = (focus?: boolean) => {
    if (!dropdownOpen.value) {
        handleDropdownOpen()
    } else {
        handleDropdownClose(undefined, undefined, focus)
    }
}

const handleArrowDownKey = () => {
    if (!dropdownOpen.value) {
        handleDropdownOpen()
    } else {
        updateHighlightedOption(findNextOptionIndex(highlightedOptionIndex.value))
    }
}

const handleArrowUpKey = () => {
    if (!dropdownOpen.value) {
        handleDropdownOpen()
    } else {
        if (highlightedOptionIndex.value === -1) {
            highlightedOptionIndex.value = 0
        } else {
            updateHighlightedOption(findPrevOptionIndex(highlightedOptionIndex.value))
        }
    }
}

const handleEnterKey = () => {
    if (!dropdownOpen.value) {
        handleDropdownOpen()
    } else {
        if (highlightedOptionIndex.value !== -1) {
            const newValue = filteredItems.value?.[highlightedOptionIndex.value]
            if (newValue) {
                handleSelect(newValue, false)
            }
        }
        if (!props.multiple) {
            handleDropdownClose(undefined, undefined, true)
        }
    }
}

const handleSpaceKey = (event: KeyboardEvent) => {
    if (!document.activeElement?.contains(searchRef.value!)) {
        event.preventDefault()
        handleContainerClick()
    }
}

const handleEscapeKey = (event: KeyboardEvent) => {
    dropdownOpen.value && event.stopPropagation()
    handleDropdownClose()
}

const handleKeyDown = (event: KeyboardEvent) => {
    if (props.disabled) {
        event.preventDefault()
        return
    }

    switch (event.code) {
        case 'Space':
            handleSpaceKey(event)
            break

        case 'Enter':
            handleEnterKey()
            event.preventDefault()
            break

        case 'ArrowDown':
            handleArrowDownKey()
            event.preventDefault()
            break

        case 'ArrowUp':
            handleArrowUpKey()
            event.preventDefault()
            break

        case 'Escape':
            handleEscapeKey(event)
            event.preventDefault()
            break

        default:
            break
    }
}

const handleOptionMouseOver = (index: number) => {
    highlightedOptionIndex.value = index
}

const handleDropdownCloseEvent = (event: Event) => {
    handleDropdownClose(event, false)
}

const handleDropdownClose = (event?: Event, stop?: boolean, focus?: boolean) => {
    if (!dropdownOpen.value) {
        return
    }

    if (stop) {
        event?.stopPropagation()
    }

    dropdownOpen.value = false
    query.value = ''
    highlightedOptionIndex.value = -1

    stopClickOutside()
    placement.value = 'bottom'
    dropdownContentHeight.value = 0

    // to keep focus of element, mostly for key handling
    if (focus) {
        nextTick(focusContainer)
    }
}

const handleBlur = async (event: FocusEvent) => {
    // wait for
    await promiseTimeout(100)

    const relatedTarget = event.relatedTarget as HTMLElement | null

    if (relatedTarget && (
        dropdownOverlay.value?.contains(relatedTarget)
        || searchRef.value && relatedTarget === searchRef.value
        || container.value?.contains(relatedTarget)
    )) {
        return
    }

    // Close the dropdown when focus truly leaves
    handleDropdownClose()
}

const focusContainer = () => {
    container.value?.querySelector<HTMLElement>('.tl-input-dropdown')?.focus()
}

const handleClear = () => {
    if (props.disabled) {
        return
    }

    model.value = props.multiple ? [] : null
    emit('clear')
    updateDropdownOverlay()
}

const onSearchChange = () => {
    highlightedOptionIndex.value = -1
}

const handleInitialValue = () => {
    if (isNil(props.initialValue)) {
        return
    }

    if (props.multiple) {
        const initialValues = (Array.isArray(props.initialValue) ? props.initialValue :
            [props.initialValue]).map(i => props.itemValue(i))
        const items = props.items?.filter((el) => initialValues.includes(props.itemValue(el)))
        model.value = clone(items)
    } else {
        const initialValue = props.itemValue(props.initialValue)
        const item = props.items?.find((el) => props.itemValue(el) === initialValue)
        model.value = clone(item) || props.label
    }
}

const focusSearch = () => {
    if (isSearchable.value) {
        searchRef.value?.focus()
    }
}

const onOverlayEnter = () => {
    overlayZindex.value = getCssVar('--layer-component-popover')
    nextTick(async () => {
        await until(isPositioned).toBeTruthy()
        focusSearch()
    })
}

const onOverlayAfterLeave = () => {
    overlayZindex.value = '-1'
}

onMounted(() => {
    if (container.value) {
        scrollParent.value = getScrollParent(container.value)
    }
    handleInitialValue()
})

watch(() => props.initialValue, (val, prev) => {
    if (!equals(val, prev)) {
        handleInitialValue()
    }
})

watch(model, (val) => {
    emit('update:modelValue', val)
})

watch(() => filteredItems.value?.length, () => {
    updateDropdownOverlay()
})

defineExpose({
    updateDropdownOverlay,
    focusSearch,
})
</script>

<style lang="sass" scoped>
@import './InputDropdown.sass'
</style>
